import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutGrid, BarChart3, LineChart, Factory, Calculator, Coins, LogOut, Settings, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle, useTheme } from "@/components/ThemeToggle";
import { BasketButton } from "@/components/BasketButton";

const LINKS = [
  { to: "/dashboards", label: "Dashboards", icon: LayoutGrid, id: "home", end: true },
  { to: "/dashboards/demand", label: "Demand & Forecast", icon: BarChart3, id: "demand" },
  { to: "/dashboards/cash-cost", label: "Cash Cost", icon: Coins, id: "ccop" },
  { to: "/dashboards/prices", label: "Price Trends", icon: LineChart, id: "prices" },
  { to: "/dashboards/capacity", label: "Capacity", icon: Factory, id: "capacity" },
  { to: "/dashboards/capex", label: "CAPEX", icon: Calculator, id: "capex" },
];

export const Logo = ({ className = "h-8" }) => {
  const { theme } = useTheme();
  return <img src={theme === "light" ? "/logo.png" : "/logo-inverse.png"} alt="CCMIntel" className={`${className} w-auto`} data-testid="brand-logo" />;
};

export default function Layout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  return (
    <div className="min-h-screen grain relative">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/80 border-b border-slate-800/80">
        <div className="px-4 md:px-8 py-3 flex items-center gap-6">
          <Link to="/" className="shrink-0" data-testid="nav-brand-link"><Logo /></Link>
          <nav className="flex items-center gap-1 overflow-x-auto flex-1">
            {LINKS.map(({ to, label, icon: Icon, id, end }) => (
              <NavLink key={id} to={to} end={end} data-testid={`nav-${id}-tab`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors duration-200 ${isActive ? "bg-[#1074BA]/15 text-white ring-1 ring-[#1074BA]/50" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <ThemeToggle testId="nav-theme-toggle" />
            <BasketButton testId="nav-basket-button" />
            <Link to="/" title="Website" data-testid="nav-website-link" className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60"><Globe className="h-4 w-4" /></Link>
            {user?.role === "admin" && <Link to="/admin" title="CMS" data-testid="nav-admin-link" className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60"><Settings className="h-4 w-4" /></Link>}
            {user ? (
              <>
                <span className="hidden xl:block font-mono text-xs text-slate-400" data-testid="nav-user-email">{user.email}</span>
                <button onClick={logout} data-testid="nav-logout-button" className="p-2 rounded-md text-slate-400 hover:text-[#F58B23] hover:bg-slate-800/60 transition-colors" title="Log out"><LogOut className="h-4 w-4" /></button>
              </>
            ) : (
              <Link to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} data-testid="nav-login-link" className="px-3 py-1.5 rounded-md bg-[#1074BA] hover:bg-[#0D629E] text-white text-sm font-medium">Login / Register</Link>
            )}
          </div>
        </div>
      </header>
      <main className="relative z-10 px-4 md:px-8 py-6 max-w-[1600px] mx-auto"><Outlet /></main>
    </div>
  );
}
